import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../data/mock';

const PerfectoCustomizationModal = ({ isOpen, onClose, item, category, onAddToCart }) => {
  // Pizza states
  const [pizzaSize, setPizzaSize] = useState('');
  const [pizzaSupplements, setPizzaSupplements] = useState([]);
  
  // Promo states
  const [selectedPizzas, setSelectedPizzas] = useState({ pizza1: '', pizza2: '', pizza3: '' });
  const [selectedSizes, setSelectedSizes] = useState({ size1: '', size2: '', size3: '' });
  
  // Tacos states
  const [tacosViandes, setTacosViandes] = useState([]);  // Changed to array for multiple meats
  const [tacosSupplements, setTacosSupplements] = useState([]);
  const [tacosSauces, setTacosSauces] = useState([]);
  
  // Burger/Sandwich/Zapdwich states
  const [selectedSauces, setSelectedSauces] = useState([]);  // Changed to array for max 2 sauces
  const [selectedDrink, setSelectedDrink] = useState('');
  
  // Menu states
  const [menuChoice, setMenuChoice] = useState('');
  const [menuDessert, setMenuDessert] = useState('');
  const [menuIngredient, setMenuIngredient] = useState('');

  if (!isOpen || !item) return null;

  // Fonction pour extraire le nom de catégorie sans emojis
  const getCategoryPrefix = (categoryName) => {
    if (!categoryName) return '';
    
    let cleanName = categoryName
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
      .replace(/BASE SAUCE TOMATE|BASE CRÈME FRAÎCHE/gi, '')
      .replace(/PIZZAS?/gi, 'Pizza')
      .replace(/MENUS?/gi, 'Menu')
      .replace(/SANDWICHS?/gi, 'Sandwich')
      .replace(/BURGERS?/gi, 'Burger')
      .replace(/RAVIOLIS?/gi, 'Raviolis')
      .replace(/PÂTES?/gi, 'Pâtes')
      .replace(/PANINIS?/gi, 'Panini')
      .replace(/SALADES?/gi, 'Salade')
      .replace(/TACOS?/gi, 'Tacos')
      .replace(/ZAPDWICHS?/gi, 'Zapdwich')
      .trim();
    
    if (categoryName.includes('MIDIS') || categoryName.includes('BAMBINO')) {
      return 'Menu';
    }
    
    return cleanName || 'Produit';
  };

  // Detect item type
  const isPizza = category?.name?.includes('PIZZAS');
  const isPromo = category?.isPromo === true;
  const isTacos = category?.name?.includes('TACOS');
  const isBurger = category?.name?.includes('BURGERS');
  const isSandwich = category?.name?.includes('SANDWICHS');
  const isZapdwich = category?.name?.includes('ZAPDWICHS');
  const isMenuMidi = category?.name?.includes('MENUS MIDIS');
  const isMenuBambino = category?.name?.includes('MENU BAMBINO');
  const isSalade = category?.name?.includes('SALADES');

  // Pizza logic
  const allPizzaIngredients = [
    ...mockData.pizzaIngredients.vegetables,
    ...mockData.pizzaIngredients.meats,
    ...mockData.pizzaIngredients.cheeses,
    ...mockData.pizzaIngredients.extras
  ];

  const getPizzaSuppCost = (size) => {
    if (size === 'Junior') return 1.00;
    if (size === 'Senior') return 1.50;
    if (size === 'Mega') return 2.00;
    return 0;
  };

  const getPizzaPrice = (pizzaName, size) => {
    const pizzaCategory = mockData.menuCategories.find(cat => 
      cat.name.includes('PIZZAS') && cat.items.some(p => p.name === pizzaName)
    );
    const pizza = pizzaCategory?.items.find(p => p.name === pizzaName);
    if (!pizza || !pizza.basePrice) return 0;
    
    const sizeKey = size.toLowerCase();
    return pizza.basePrice[sizeKey] || 0;
  };

  // Promo logic
  const getAllPizzaNames = () => {
    const tomatoBase = mockData.menuCategories.find(cat => cat.name.includes('PIZZAS BASE SAUCE TOMATE'));
    const cremeBase = mockData.menuCategories.find(cat => cat.name.includes('PIZZAS BASE CRÈME'));
    return [
      ...(tomatoBase?.items || []).map(p => p.name),
      ...(cremeBase?.items || []).map(p => p.name)
    ];
  };

  const handlePizzaSuppToggle = (supplement) => {
    setPizzaSupplements(prev => {
      if (prev.includes(supplement)) {
        return prev.filter(s => s !== supplement);
      } else {
        return [...prev, supplement];
      }
    });
  };

  const handleTacosSauceToggle = (sauce) => {
    setTacosSauces(prev => {
      if (prev.includes(sauce)) {
        return prev.filter(s => s !== sauce);
      } else if (prev.length < 2) {
        return [...prev, sauce];
      } else {
        // Replace the first sauce when limit reached
        return [prev[1], sauce];
      }
    });
  };

  const handleTacosViandeToggle = (viande) => {
    setTacosViandes(prev => {
      if (prev.includes(viande)) {
        return prev.filter(v => v !== viande);
      } else {
        return [...prev, viande];
      }
    });
  };

  const handleSauceToggle = (sauce) => {
    setSelectedSauces(prev => {
      if (prev.includes(sauce)) {
        return prev.filter(s => s !== sauce);
      } else if (prev.length < 2) {
        return [...prev, sauce];
      } else {
        // Replace the first sauce when limit reached
        return [prev[1], sauce];
      }
    });
  };

  const handleTacosSupplementToggle = (supp) => {
    setTacosSupplements(prev => {
      if (prev.includes(supp)) {
        return prev.filter(s => s !== supp);
      } else {
        return [...prev, supp];
      }
    });
  };

  const calculatePrice = () => {
    let basePrice = parseFloat(item.price.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
    let extraCost = 0;

    if (isPizza && pizzaSize) {
      basePrice = getPizzaPrice(item.name, pizzaSize);
      extraCost = pizzaSupplements.length * getPizzaSuppCost(pizzaSize);
    }

    if (isTacos) {
      // Extra meat cost: 1€ per meat after the first one
      if (tacosViandes.length > 1) {
        extraCost += (tacosViandes.length - 1) * 1.00;
      }
      
      // Supplement costs
      tacosSupplements.forEach(supp => {
        if (supp.includes('cheddar') || supp.includes('reblochon')) {
          extraCost += 0.70;
        }
      });
    }

    return basePrice + extraCost;
  };

  const formatOptionsText = () => {
    const parts = [];

    if (isPizza && pizzaSize) {
      parts.push(`Taille: ${pizzaSize}`);
      if (pizzaSupplements.length > 0) {
        const suppCost = getPizzaSuppCost(pizzaSize);
        const suppText = pizzaSupplements.map(s => `${s} (+${suppCost.toFixed(2)}€)`).join(', ');
        parts.push(`Suppléments: ${suppText}`);
      }
    }

    if (isTacos) {
      if (tacosViandes.length > 0) {
        const viandesText = tacosViandes.map((v, idx) => 
          idx === 0 ? v : `${v} (+1€)`
        ).join(', ');
        parts.push(`Viandes: ${viandesText}`);
      }
      if (tacosSupplements.length > 0) {
        parts.push(`Suppléments: ${tacosSupplements.join(', ')}`);
      }
      if (tacosSauces.length > 0) {
        parts.push(`Sauces: ${tacosSauces.join(', ')}`);
      }
    }

    if ((isBurger || isSandwich || isZapdwich) && selectedSauces.length > 0) {
      parts.push(`Sauces: ${selectedSauces.join(', ')}`);
    }

    if (selectedDrink) {
      parts.push(`Boisson: ${selectedDrink}`);
    }

    if (isMenuMidi && menuChoice) {
      parts.push(`Plat: ${menuChoice}`);
      if (menuDessert) parts.push(`Dessert: ${menuDessert}`);
    }

    if (isMenuBambino && menuIngredient) {
      parts.push(`Ingrédient: ${menuIngredient}`);
    }

    return parts.join(' • ');
  };

  const isValidSelection = () => {
    if (isPizza && !pizzaSize) return false;
    if (isPromo) {
      const promoType = item.promoType;
      if (promoType === '3_junior_pizzas') {
        return selectedPizzas.pizza1 && selectedPizzas.pizza2 && selectedPizzas.pizza3;
      }
      if (promoType === '2_senior_pizzas' || promoType === '2_mega_pizzas') {
        return selectedPizzas.pizza1 && selectedPizzas.pizza2;
      }
    }
    if (isTacos && tacosViandes.length === 0) return false;
    if ((isBurger || isSandwich || isZapdwich) && selectedSauces.length === 0) return false;
    if (isMenuMidi && !menuChoice) return false;
    if (isMenuBambino && !menuIngredient) return false;
    return true;
  };

  const handleAddToCart = () => {
    if (!isValidSelection()) {
      alert('Veuillez compléter tous les choix obligatoires');
      return;
    }

    // Ajouter le préfixe de catégorie
    const categoryPrefix = getCategoryPrefix(category.name);
    let finalName = `${categoryPrefix} – ${item.name}`;
    
    const optionsText = formatOptionsText();
    if (optionsText) {
      finalName = `${categoryPrefix} – ${item.name} - ${optionsText}`;
    }

    // Promo handling
    if (isPromo) {
      const promoType = item.promoType;
      let promoDetails = [];
      
      if (promoType === '3_junior_pizzas') {
        promoDetails.push(`Pizza 1: ${selectedPizzas.pizza1}`);
        promoDetails.push(`Pizza 2: ${selectedPizzas.pizza2}`);
        promoDetails.push(`Pizza 3: ${selectedPizzas.pizza3}`);
      } else if (promoType === '2_senior_pizzas') {
        promoDetails.push(`Pizza 1: ${selectedPizzas.pizza1} (Senior)`);
        promoDetails.push(`Pizza 2: ${selectedPizzas.pizza2} (Senior)`);
      } else if (promoType === '2_mega_pizzas') {
        promoDetails.push(`Pizza 1: ${selectedPizzas.pizza1} (Mega)`);
        promoDetails.push(`Pizza 2: ${selectedPizzas.pizza2} (Mega)`);
      }
      
      finalName = `${item.name} - ${promoDetails.join(' • ')} • Coca 1,5L inclus`;
    }

    const customizedItem = {
      id: `${category.name}-${item.name}`.replace(/\s+/g, '-').toLowerCase() + `-${Date.now()}`,
      name: finalName,
      price: `${calculatePrice().toFixed(2)} €`,
      description: item.description,
      category: category.name,
      quantity: 1,
      originalName: item.name,
      options: {
        pizzaSize,
        pizzaSupplements,
        selectedPizzas,
        tacosViandes,
        tacosSupplements,
        tacosSauces,
        selectedSauces,
        selectedDrink,
        menuChoice,
        menuDessert,
        menuIngredient,
        optionsText
      }
    };

    onAddToCart(customizedItem);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setPizzaSize('');
    setPizzaSupplements([]);
    setSelectedPizzas({ pizza1: '', pizza2: '', pizza3: '' });
    setSelectedSizes({ size1: '', size2: '', size3: '' });
    setTacosViandes([]);
    setTacosSupplements([]);
    setTacosSauces([]);
    setSelectedSauces([]);
    setSelectedDrink('');
    setMenuChoice('');
    setMenuDessert('');
    setMenuIngredient('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-testid="customization-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800" data-testid="modal-title">
            Personnaliser {item.name}
          </h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700" data-testid="modal-close-btn">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Item Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900" data-testid="modal-item-name">{item.name}</h3>
            <p className="text-sm text-gray-600 mt-1" data-testid="modal-item-desc">{item.description}</p>
            <p className="text-[#D47A2A] font-bold mt-2" data-testid="modal-base-price">Prix de base: {item.price}</p>
          </div>

          {/* PIZZA CUSTOMIZATION */}
          {isPizza && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="pizza-size-title">
                  Choisissez votre taille *
                </h4>
                <div className="space-y-2">
                  {['Junior', 'Senior', 'Mega'].map((size) => {
                    const price = getPizzaPrice(item.name, size);
                    return (
                      <button
                        key={size}
                        onClick={() => setPizzaSize(size)}
                        className={`w-full p-3 text-left border rounded-lg transition-colors ${
                          pizzaSize === size
                            ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                            : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                        }`}
                        data-testid={`pizza-size-${size.toLowerCase()}`}
                      >
                        <div className="flex justify-between items-center">
                          <span>{size}</span>
                          <span className="font-bold">{price.toFixed(2)}€</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {!pizzaSize && (
                  <p className="text-red-600 text-sm mt-2" data-testid="pizza-size-error">Veuillez sélectionner une taille</p>
                )}
              </div>

              {pizzaSize && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3" data-testid="pizza-supplements-title">
                    Suppléments (facultatif)
                    <span className="text-sm font-normal text-gray-600 ml-2">
                      (+{getPizzaSuppCost(pizzaSize).toFixed(2)}€ chacun)
                    </span>
                  </h4>
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {allPizzaIngredients.map((ingredient) => (
                      <label
                        key={ingredient}
                        className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                          pizzaSupplements.includes(ingredient)
                            ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                            : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                        }`}
                        data-testid={`pizza-supplement-${ingredient.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <input
                          type="checkbox"
                          checked={pizzaSupplements.includes(ingredient)}
                          onChange={() => handlePizzaSuppToggle(ingredient)}
                          className="sr-only"
                        />
                        <span className="text-sm">{ingredient}</span>
                      </label>
                    ))}
                  </div>
                  {pizzaSupplements.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2" data-testid="pizza-supplements-count">
                      {pizzaSupplements.length} supplément(s) sélectionné(s) (+{(pizzaSupplements.length * getPizzaSuppCost(pizzaSize)).toFixed(2)}€)
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PROMO CUSTOMIZATION */}
          {isPromo && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="promo-pizzas-title">
                  Choisissez vos pizzas *
                </h4>
                {item.promoType === '3_junior_pizzas' && (
                  <div className="space-y-4">
                    {['pizza1', 'pizza2', 'pizza3'].map((pizzaKey, idx) => (
                      <div key={pizzaKey}>
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-testid={`promo-label-${pizzaKey}`}>
                          Pizza {idx + 1} (Junior)
                        </label>
                        <select
                          value={selectedPizzas[pizzaKey]}
                          onChange={(e) => setSelectedPizzas({ ...selectedPizzas, [pizzaKey]: e.target.value })}
                          className="w-full p-2 border border-gray-300 rounded-lg text-gray-900"
                          data-testid={`promo-select-${pizzaKey}`}
                        >
                          <option value="">-- Choisir une pizza --</option>
                          {getAllPizzaNames().map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
                {(item.promoType === '2_senior_pizzas' || item.promoType === '2_mega_pizzas') && (
                  <div className="space-y-4">
                    {['pizza1', 'pizza2'].map((pizzaKey, idx) => (
                      <div key={pizzaKey}>
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-testid={`promo-label-${pizzaKey}`}>
                          Pizza {idx + 1} ({item.promoType === '2_senior_pizzas' ? 'Senior' : 'Mega'})
                        </label>
                        <select
                          value={selectedPizzas[pizzaKey]}
                          onChange={(e) => setSelectedPizzas({ ...selectedPizzas, [pizzaKey]: e.target.value })}
                          className="w-full p-2 border border-gray-300 rounded-lg text-gray-900"
                          data-testid={`promo-select-${pizzaKey}`}
                        >
                          <option value="">-- Choisir une pizza --</option>
                          {getAllPizzaNames().map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TACOS CUSTOMIZATION */}
          {isTacos && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="tacos-viande-title">
                  Choix des viandes * (multiple possible, +1€ par viande supplémentaire)
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {['grec', 'chicken', 'viande hachée', 'merguez', 'nuggets', 'cordon bleu', 'tenders'].map((viande) => (
                    <label
                      key={viande}
                      className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                        tacosViandes.includes(viande)
                          ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                          : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                      }`}
                      data-testid={`tacos-viande-${viande.replace(/\s+/g, '-')}`}
                    >
                      <input
                        type="checkbox"
                        checked={tacosViandes.includes(viande)}
                        onChange={() => handleTacosViandeToggle(viande)}
                        className="sr-only"
                      />
                      <span className="text-sm capitalize">{viande}</span>
                    </label>
                  ))}
                </div>
                {tacosViandes.length > 1 && (
                  <p className="text-sm text-gray-600 mt-2" data-testid="tacos-viandes-count">
                    {tacosViandes.length - 1} viande(s) supplémentaire(s) (+{((tacosViandes.length - 1) * 1.00).toFixed(2)}€)
                  </p>
                )}
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="tacos-supplements-title">
                  Suppléments (facultatif)
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border rounded-lg hover:border-[#D47A2A] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tacosSupplements.includes('cheddar')}
                      onChange={() => handleTacosSupplementToggle('cheddar')}
                      className="mr-3 h-4 w-4"
                      data-testid="tacos-supplement-cheddar"
                    />
                    <span className="text-gray-900">Cheddar</span>
                    <span className="ml-auto text-sm text-gray-900">+0.70€</span>
                  </label>
                  <label className="flex items-center p-3 border rounded-lg hover:border-[#D47A2A] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tacosSupplements.includes('reblochon')}
                      onChange={() => handleTacosSupplementToggle('reblochon')}
                      className="mr-3 h-4 w-4"
                      data-testid="tacos-supplement-reblochon"
                    />
                    <span className="text-gray-900">Reblochon</span>
                    <span className="ml-auto text-sm text-gray-900">+0.70€</span>
                  </label>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="tacos-sauces-title">
                  Sauces (max 2)
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {mockData.sauces.map((sauce) => (
                    <label
                      key={sauce}
                      className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                        tacosSauces.includes(sauce)
                          ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                          : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                      } ${
                        tacosSauces.length >= 2 && !tacosSauces.includes(sauce)
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      data-testid={`tacos-sauce-${sauce.toLowerCase()}`}
                    >
                      <input
                        type="checkbox"
                        checked={tacosSauces.includes(sauce)}
                        onChange={() => handleTacosSauceToggle(sauce)}
                        disabled={tacosSauces.length >= 2 && !tacosSauces.includes(sauce)}
                        className="sr-only"
                      />
                      <span className="text-sm">{sauce}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* BURGER/SANDWICH/ZAPDWICH CUSTOMIZATION */}
          {(isBurger || isSandwich || isZapdwich) && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="sauce-choice-title">
                  Choix des sauces * (max 2)
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {mockData.sauces.map((sauce) => (
                    <label
                      key={sauce}
                      className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors ${
                        selectedSauces.includes(sauce)
                          ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                          : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                      } ${
                        selectedSauces.length >= 2 && !selectedSauces.includes(sauce)
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      data-testid={`sauce-${sauce.toLowerCase()}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSauces.includes(sauce)}
                        onChange={() => handleSauceToggle(sauce)}
                        disabled={selectedSauces.length >= 2 && !selectedSauces.includes(sauce)}
                        className="sr-only"
                      />
                      <span className="text-sm">{sauce}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="drink-choice-title">Choix de la boisson *</h4>
                <div className="space-y-2">
                  {mockData.drinks33cl.map((drink) => (
                    <button
                      key={drink}
                      onClick={() => setSelectedDrink(drink)}
                      className={`w-full p-3 text-left border rounded-lg transition-colors ${
                        selectedDrink === drink
                          ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                          : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                      }`}
                      data-testid={`drink-${drink.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {drink}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MENU MIDI CUSTOMIZATION */}
          {isMenuMidi && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="menu-midi-choice-title">Choix du plat *</h4>
                {item.menuType === 'pizza_midi' && (
                  <select
                    value={menuChoice}
                    onChange={(e) => setMenuChoice(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-gray-900"
                    data-testid="menu-midi-pizza-select"
                  >
                    <option value="">-- Choisir une pizza Junior --</option>
                    {getAllPizzaNames().map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                )}
                {item.menuType === 'salade_midi' && (
                  <select
                    value={menuChoice}
                    onChange={(e) => setMenuChoice(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-gray-900"
                    data-testid="menu-midi-salade-select"
                  >
                    <option value="">-- Choisir une salade --</option>
                    {mockData.menuCategories.find(cat => cat.name.includes('SALADES'))?.items.map(item => (
                      <option key={item.name} value={item.name}>{item.name}</option>
                    ))}
                  </select>
                )}
                {item.menuType === 'pates_midi' && (
                  <select
                    value={menuChoice}
                    onChange={(e) => setMenuChoice(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-gray-900"
                    data-testid="menu-midi-pates-select"
                  >
                    <option value="">-- Choisir un plat de pâtes --</option>
                    {mockData.menuCategories.find(cat => cat.name.includes('PÂTES'))?.items.map(item => (
                      <option key={item.name} value={item.name}>{item.name}</option>
                    ))}
                  </select>
                )}
                {item.menuType === 'sandwich_midi' && (
                  <select
                    value={menuChoice}
                    onChange={(e) => setMenuChoice(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-gray-900"
                    data-testid="menu-midi-sandwich-select"
                  >
                    <option value="">-- Choisir un sandwich --</option>
                    {mockData.menuCategories.find(cat => cat.name.includes('SANDWICHS'))?.items.map(item => (
                      <option key={item.name} value={item.name}>{item.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="menu-midi-dessert-title">Choix du dessert *</h4>
                <select
                  value={menuDessert}
                  onChange={(e) => setMenuDessert(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-gray-900"
                  data-testid="menu-midi-dessert-select"
                >
                  <option value="">-- Choisir un dessert (sauf glace) --</option>
                  {mockData.menuCategories.find(cat => cat.name.includes('DESSERTS'))?.items.map(item => (
                    <option key={item.name} value={item.name}>{item.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="menu-midi-boisson-title">Choix de la boisson 33cl *</h4>
                <div className="space-y-2">
                  {mockData.drinks33cl.map((drink) => (
                    <button
                      key={drink}
                      onClick={() => setSelectedDrink(drink)}
                      className={`w-full p-3 text-left border rounded-lg transition-colors ${
                        selectedDrink === drink
                          ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                          : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                      }`}
                      data-testid={`menu-midi-drink-${drink.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {drink}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MENU BAMBINO CUSTOMIZATION */}
          {isMenuBambino && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3" data-testid="menu-bambino-ingredient-title">
                  Choix de l'ingrédient supplémentaire *
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {allPizzaIngredients.slice(0, 12).map((ingredient) => (
                    <button
                      key={ingredient}
                      onClick={() => setMenuIngredient(ingredient)}
                      className={`p-2 text-left border rounded-lg transition-colors ${
                        menuIngredient === ingredient
                          ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                          : 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                      }`}
                      data-testid={`bambino-ingredient-${ingredient.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <span className="text-sm">{ingredient}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Inclus:</span> Mini pizza Classica + 5 nuggets + frites + 1 Kinder + 1 Caprisun
                </p>
              </div>
            </div>
          )}

          {/* SALADES CUSTOMIZATION */}
          {isSalade && (
            <div className="space-y-6">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Pain offert</span>
                </p>
              </div>
            </div>
          )}

          {/* Options Summary */}
          {formatOptionsText() && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4">
              <h4 className="font-medium text-gray-900 mb-2" data-testid="options-summary-title">Votre sélection:</h4>
              <p className="text-sm text-gray-700" data-testid="options-summary-text">{formatOptionsText()}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm text-gray-600" data-testid="total-label">Total</p>
              <p className="text-xl font-bold text-[#D47A2A]" data-testid="total-price">
                {calculatePrice().toFixed(2)}€
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-gray-400 text-gray-900 bg-white hover:bg-gray-100 hover:text-gray-900"
              data-testid="modal-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={handleAddToCart}
              disabled={!isValidSelection()}
              className="flex-1 bg-[#D47A2A] hover:bg-[#B8661F] text-white disabled:opacity-50"
              data-testid="modal-add-to-cart-btn"
            >
              Ajouter au panier
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerfectoCustomizationModal;
